import { CircleCheck, TriangleAlert } from 'lucide-react';

import { useCoordinationPreconditionsQuery } from '@/modules/coordination/queries/useCoordinationPreconditionsQuery';
import {
  Callout,
  CalloutDescription,
  CalloutTitle,
} from '@/modules/ui/components/callout/Callout';
import type { components } from '@/types/apiSchema';

interface CoordinationPreconditionsCheckProps {
  jobId: string;
}

function formatUser(
  user: components['schemas']['CoordinationPreconditionsUserResponse'],
) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

  if (name && user.email) {
    return `${name} (${user.email})`;
  }

  return name || user.email || 'Unknown user';
}

function formatUsers(
  users: components['schemas']['CoordinationPreconditionsUserResponse'][],
) {
  return users.length === 0 ? 'Not assigned' : users.map(formatUser).join(', ');
}

export function CoordinationPreconditionsCheck({
  jobId,
}: CoordinationPreconditionsCheckProps) {
  const preconditionsQuery = useCoordinationPreconditionsQuery(jobId);
  const preconditions = preconditionsQuery.data;

  if (!preconditions) {
    return null;
  }

  const variant = preconditions.preconditionsMet ? 'success' : 'warning';

  return (
    <Callout variant={variant}>
      <CalloutTitle className="flex items-center gap-2">
        {preconditions.preconditionsMet ? (
          <CircleCheck className="size-4 text-[#52c41a]" />
        ) : (
          <TriangleAlert className="size-4 text-[#faad14]" />
        )}
        {preconditions.preconditionsMet
          ? 'Coordination preconditions met'
          : 'Coordination preconditions not met'}
      </CalloutTitle>
      <CalloutDescription>
        Recruiter: {formatUsers(preconditions.recruiters)}
        {!preconditions.recruiterAssigned && ' (expected exactly 1)'}
        <br />
        Hiring manager: {formatUsers(preconditions.hiringManagers)}
        {!preconditions.hiringManagerAssigned && ' (expected exactly 1)'}
      </CalloutDescription>
    </Callout>
  );
}
